/**
 * Keeps the price inside the PDP add-to-cart button in sync with the Recharge
 * subscription widget.
 *
 * The widget mounts client side and swaps the selling plan without re-rendering
 * the section, so the price Liquid put in the button goes stale the moment the
 * shopper switches between "Subscribe & save" and "One-time purchase". The
 * theme's own variant handling (VariantSelects.toggleAddButton) also rewrites
 * that price with the one-time amount on every variant change, so the plan
 * price has to be re-applied afterwards.
 */
if (!customElements.get('subscription-price')) {
  customElements.define(
    'subscription-price',
    class SubscriptionPrice extends HTMLElement {
      constructor() {
        super();
        this.sync = this.sync.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);
        this.handleChange = this.handleChange.bind(this);
      }

      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) return;

        const dataElement = document.getElementById(this.dataset.priceData);
        if (!dataElement) return;

        try {
          this.prices = JSON.parse(dataElement.textContent);
        } catch (error) {
          return;
        }

        this.info = document.getElementById(this.dataset.infoId);

        // Recharge tears the widget down and rebuilds it, so listen on the
        // document instead of on inputs that may not exist yet.
        document.addEventListener('change', this.handleChange);
        document.addEventListener('click', this.handleChange);

        // Covers the widget mounting, the theme rewriting the button on variant
        // change, and Recharge updating the hidden selling_plan input by
        // property assignment (which fires no event).
        this.observer = new MutationObserver(this.scheduleSync);
        this.observer.observe(this.form, { childList: true, subtree: true });

        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.handleChange);
        document.removeEventListener('click', this.handleChange);
        if (this.observer) this.observer.disconnect();
        clearTimeout(this.syncTimer);
      }

      handleChange(event) {
        const target = event.target;
        if (!target || !target.closest) return;

        const isWidgetControl =
          target.name === 'purchaseOption' ||
          target.name === 'rc_selling_plan' ||
          target.name === 'selling_plan' ||
          target.closest('[class*="rc-radio"], [class*="rc-selling-plans"]');

        // The widget updates its own state after the click, so let it settle.
        if (isWidgetControl) this.scheduleSync();
      }

      scheduleSync() {
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(this.sync, 100);
      }

      sync() {
        const priceElement = this.form.querySelector('.product-form__submit .price');
        if (!priceElement) return;

        const variantInput = this.form.querySelector('input[name="id"]');
        const entry = variantInput && this.prices[variantInput.value];
        if (!entry) return;

        const planId = this.getSelectedPlanId();
        const planPrice = planId ? entry.plans[planId] : null;

        let price;
        let compareAt;

        if (planPrice != null) {
          // The subscription discount is shown against the one-time price, the
          // same comparison the widget itself makes.
          price = planPrice;
          compareAt = Math.max(entry.compare_at || 0, entry.onetime);
        } else {
          price = entry.onetime;
          compareAt = entry.compare_at > entry.onetime ? entry.compare_at : null;

          const discountPercent = Number(this.info?.dataset.visualDiscountPercent || 0);
          const discountEnabled =
            this.info?.dataset.visualDiscountButtonEnabled === 'true' &&
            this.info?.dataset.noVisualDiscount !== 'true';

          if (!compareAt && discountEnabled && discountPercent > 0) {
            compareAt = price;
            price = Math.round((price * (100 - discountPercent)) / 100);
          }
        }

        this.write(priceElement, this.buildMarkup(price, compareAt > price ? compareAt : null));
      }

      getSelectedPlanId() {
        // What the shopper actually sees wins. Recharge drives the radios with
        // Vue, so the checked property is set but the attribute is not, and the
        // active option is additionally flagged with a class.
        const checked = this.query('input[name="purchaseOption"]:checked');
        const active = this.query('.rc-radio--active input[name="purchaseOption"]');
        const selected = checked || active;

        if (selected) {
          if (selected.value === 'onetime') return null;
          const select = this.query('select[name="rc_selling_plan"]');
          return (select && select.value) || this.firstPlanId();
        }

        // Before the widget mounts, fall back to the plan Recharge will submit.
        const planInput = this.query('input[name="selling_plan"]');
        return (planInput && planInput.value) || null;
      }

      firstPlanId() {
        const variantInput = this.form.querySelector('input[name="id"]');
        const entry = variantInput && this.prices[variantInput.value];
        const ids = entry ? Object.keys(entry.plans) : [];
        return ids.length ? ids[0] : null;
      }

      query(selector) {
        // The widget normally renders inside the form, but Recharge can be
        // configured to inject it elsewhere on the page.
        return this.form.querySelector(selector) || document.querySelector(selector);
      }

      buildMarkup(price, compareAt) {
        const money = this.formatMoney;

        if (compareAt) {
          return (
            '<div class="price__container"><div class="price__wrap price__sale">' +
            '<span class="hidden">Regular price</span>' +
            '<span><s class="price-item price-item--regular strike-price relative inline-block mr-1">' +
            money(compareAt) +
            '</s></span>' +
            '<span class="hidden">Sale price</span>' +
            '<span class="price-item price-item--sale price-item--last inline-block">' +
            money(price) +
            '</span></div></div>'
          );
        }

        return (
          '<div class="price__container"><div class="price__wrap price__regular">' +
          '<span class="hidden">Regular price</span>' +
          '<span class="price-item price-item--regular">' +
          money(price) +
          '</span></div></div>'
        );
      }

      // Matches Liquid's money_without_trailing_zeros so the value the browser
      // writes looks the same as the one the server rendered.
      formatMoney(cents) {
        return formatShopMoney(cents, true);
      }

      write(element, markup) {
        if (element.innerHTML === markup) return;

        // Our own write would otherwise re-trigger the observer.
        this.observer.disconnect();
        element.innerHTML = markup;
        this.observer.observe(this.form, { childList: true, subtree: true });
      }
    }
  );
}
